/* ============================================================
   Lost Collective · Scrollytelling subsystem
   File: assets/lc-scrollytelling.js

   Handles three pieces of the scrolly experience:
     1. Caption hydration — extracts paragraphs that follow each
        chapter h2, splits long ones at sentence boundaries, and
        injects them as .lc-scrolly__caption nodes inside the
        matching .lc-scrolly[data-anchor].
     2. Image layer + paper sections (interleave mode only) —
        #lc-scrolly-img is a viewport-filling fixed background
        layer; .lc-scrolly-paper-section rectangles cover every
        body-y range NOT inside a .lc-scrolly so the body text
        reads against paper, not the photograph.
     3. Caption-driven image transitions — IntersectionObserver +
        rAF-throttled scroll listener pick the caption nearest
        viewport centre; the matching frame is swapped into the
        fixed layer via opacity crossfade.

   Two modes, switched by data-scrolly-mode on <article> (default
   "interleave"):
     interleave — multiple .lc-scrolly chapters separated by
                  paper-text sections (the Wangi pattern).
     immersive  — single sticky stage spanning the entire article;
                  paper sections suppressed; body text overlays
                  the photograph throughout.

   Mobile (≤720px) runs the SAME architecture as desktop — the
   image stays stationary as a fixed viewport-filling background
   while paper sections and captions scroll over it. Only
   prefers-reduced-motion skips the fixed overlay and renders
   scrolly blocks as stacked inline sections with captions below
   each image (CSS handles the static fallback layout).

   Waits on window.LCArticle.onReady (fired by lc-article-postprocess
   after Pass 1 rebuild + Pass 10 figure placement) so chapter
   headings and figures are in place before scrolly wiring runs.

   CI coverage (LOS7-2922). This file is gated by tests/scrolly.spec.ts,
   which runs as the dom-tests job in .github/workflows/ci.yml on every
   PR. That spec drives THIS file over tests/fixtures/scrolly-harness.html
   on file://, so it reads the working copy and needs no theme, no store
   session and no network. It is the only gate that can see a change here
   before it deploys: every step in article-qa.yml loads the live site, so
   a regression on a feature branch is invisible to all of them. Run it
   locally with `npm run test:dom`.

   When you add a mode, a frame_map or a data- attribute, add the case to
   that spec. The harness builds chapters from a query string, so a new
   case is usually one more parameter rather than a new fixture.
   ============================================================ */

(function () {
  'use strict';

  // ----- Debug switch -------------------------------------------------------
  // LOS7-2915. Two comments in this subsystem have documented `?lcdebug` as
  // the switch that turns on the phase-table rejection reasons and the
  // window.__lcActive beat readout, and nothing has ever read it: the flag
  // could only be set by typing it into a console, and only in the window
  // between script execution and the first phase compile. A rejected cue
  // table falls back silently to the legacy whole-journey draw, so on the
  // live site a broken table and a healthy one looked identical, which is
  // what made the Ikeshima chapters unverifiable from outside.
  //
  // Set here, at the top of the file, so it is true before init() runs and
  // before compilePhases() is first called. A page loaded without the
  // parameter sets nothing and logs nothing, so production readers still pay
  // nothing. An explicit window.LC_SCROLLY_DEBUG set before this script loads
  // is left alone.
  if (!window.LC_SCROLLY_DEBUG) {
    try {
      var lcDebugParams = new URLSearchParams(window.location.search);
      if (lcDebugParams.has('lcdebug')) window.LC_SCROLLY_DEBUG = true;
    } catch (_e) {
      // URLSearchParams is everywhere this theme supports; if it ever throws,
      // debug simply stays off, which is the production default anyway.
    }
  }

  // ----- Shared coordination ------------------------------------------------
  window.LCArticle = window.LCArticle || (function () {
    var state = { ready: false, queue: [] };
    return {
      onReady: function (fn) {
        if (state.ready) fn();
        else state.queue.push(fn);
      },
      fireReady: function () {
        if (state.ready) return;
        state.ready = true;
        state.queue.forEach(function (fn) { try { fn(); } catch (_e) {} });
        state.queue.length = 0;
      }
    };
  })();

  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  // ----- Mode resolution ----------------------------------------------------
  function resolveMode() {
    var article = document.querySelector('article.article');
    var explicit = article && article.getAttribute('data-scrolly-mode');
    if (explicit === 'immersive' || explicit === 'interleave') return explicit;
    var bodyAttr = document.body.getAttribute('data-scrolly-mode');
    if (bodyAttr === 'immersive' || bodyAttr === 'interleave') return bodyAttr;
    return 'interleave';
  }

  // ----- Caption hydration --------------------------------------------------
  // Every cue string in a chapter's phase table (cue, zoom.until_cue, and
  // any other *_cue key at any depth), normalised. The short-card merge below must never fold
  // away a card one of these addresses: One day's zoom until_cue points at a
  // six-word closing chunk, "Just posters and noticeboards...", and merging it
  // made the whole table fail shut.
  function phaseCues(scrollyEl) {
    var raw = scrollyEl.getAttribute('data-phases');
    if (!raw) return [];
    var out = [];
    // Walked, not read flat: until_cue lives inside a phase's zoom object.
    function walk(o) {
      if (!o || typeof o !== 'object') return;
      Object.keys(o).forEach(function (k) {
        if ((k === 'cue' || /_cue$/.test(k)) && typeof o[k] === 'string') out.push(normCue(o[k]));
        else walk(o[k]);
      });
    }
    try { walk(JSON.parse(raw).phases || []); } catch (_e) { /* rejected later, loudly */ }
    return out;
  }

  function normCue(t) {
    return String(t || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function hydrateCaptions(root, scrollyEl) {
    var anchor = scrollyEl.getAttribute('data-anchor');
    var protectedCues = phaseCues(scrollyEl);
    var captionsContainer = scrollyEl.querySelector('.lc-scrolly__captions');
    if (!captionsContainer || captionsContainer.children.length) return null;

    // A chapter can pin, release back to ordinary paper flow for a beat, then
    // pin again under the SAME heading -- e.g. two photographs sitting inline
    // between two halves of one map story -- rather than being forced into a
    // second numbered TOC chapter just to get a pause. The second half's
    // anchor carries a '-return' suffix; stripped, it resolves to the same h2
    // as the first half. Two markers authored in the body,
    // .lc-scrolly-split-end and .lc-scrolly-split-start, bound the paper-flow
    // gap between the two halves' caption ranges.
    var isReturn = /-return$/.test(anchor);
    var h2Anchor = isReturn ? anchor.slice(0, -'-return'.length) : anchor;
    var targetH2 = findH2ByAnchor(root, h2Anchor);
    if (!targetH2) return null;

    var paragraphsToMove = collectChapterItems(targetH2, isReturn
      ? { startAfterClass: 'lc-scrolly-split-start' }
      : { stopAtClass: 'lc-scrolly-split-end' });
    var captionContents = [];

    // Position the sticky section just before the first caption's top-level
    // container, NOT at targetH2.nextSibling. Anything sitting between the
    // heading and the first caption — e.g. a <figure> intro paragraph — then
    // stays beneath the heading, on the paper background, before the sticky
    // sequence begins. Done before the caption paragraphs are removed so the
    // anchor is still connected. Falls back to targetH2.nextSibling when the
    // section has no captions.
    if (scrollyEl.parentNode !== root) {
      var insertAnchor = targetH2.nextSibling;
      if (paragraphsToMove.length) {
        var topContainer = paragraphsToMove[0].node;
        while (topContainer && topContainer.parentNode !== root) {
          topContainer = topContainer.parentNode;
        }
        if (topContainer && topContainer.parentNode === root) insertAnchor = topContainer;
      }
      root.insertBefore(scrollyEl, insertAnchor);
    }

    paragraphsToMove.forEach(function (item, origIdx) {
      var p = item.node;
      if (item.kind === 'exchange') {
        // LOS7-2726. An exchange that follows prose is a beat in the caption
        // sequence, not paper furniture: it becomes ONE caption card carrying
        // the .lc-exchange treatment (hanging indent, ochre rules) at its own
        // paragraph index, so it keeps its place in the narrative and its
        // frame. Never sentence-split: the line breaks are the speaker turns.
        // The card holds a nested .lc-exchange so the treatment comes from the
        // one place it is defined (lc-article.css), not a second copy of it.
        captionContents.push({
          html: '<div class="lc-exchange">' + p.innerHTML + '</div>',
          bgIdx: origIdx,
          chunkNo: 0,
          exchange: true
        });
      } else {
        var chunks = sentenceSplit((p.textContent || '').trim(), 3, protectedCues);
        if (chunks) {
          chunks.forEach(function (c, ci) {
            captionContents.push({ html: c, bgIdx: origIdx, chunkNo: ci });
          });
        } else {
          captionContents.push({ html: p.innerHTML, bgIdx: origIdx, chunkNo: 0 });
        }
      }
      var parent = p.parentNode;
      if (!parent) return;
      parent.removeChild(p);
      if (
        parent.classList &&
        parent.classList.contains('prose-block') &&
        !parent.textContent.trim() &&
        parent.parentNode
      ) {
        parent.parentNode.removeChild(parent);
      }
    });

    captionContents.forEach(function (item, i) {
      var captionDiv = document.createElement('div');
      captionDiv.className = 'lc-scrolly__caption' +
        (item.exchange ? ' lc-scrolly__caption--exchange' : '');
      captionDiv.setAttribute('data-scrolly-index', String(item.bgIdx));
      // data-scrolly-index is the SOURCE PARAGRAPH index, so the chunks of one
      // split paragraph all share it and cannot be told apart. The phase table
      // needs to address a single card, so stamp its dense ordinal within the
      // chapter and its chunk number within its paragraph. Nothing in the
      // resolver reads either: data-scrolly-index keeps its value and meaning,
      // so the default, 'caption' and 'spread' frame maps are untouched.
      captionDiv.setAttribute('data-scrolly-card', String(i));
      captionDiv.setAttribute('data-scrolly-chunk', String(item.chunkNo || 0));
      captionDiv.innerHTML = item.html;
      captionsContainer.appendChild(captionDiv);
    });

    targetH2.classList.add('lc-h2-pre-scrolly');

    return { scrollyEl: scrollyEl, targetH2: targetH2, captionCount: captionContents.length };
  }

  function findH2ByAnchor(root, anchor) {
    var found = null;
    root.querySelectorAll('h2.h2-mark').forEach(function (h2) {
      var ttl = h2.querySelector('.ttl');
      if (!ttl) return;
      var slug = ttl.textContent.trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (slug === anchor || h2.id === anchor) found = h2;
    });
    return found;
  }

  // Everything between this chapter's h2 and the next one that belongs in the
  // caption sequence, in source order. Each item is { node, kind }:
  //   'p'        an ordinary body paragraph (sentence-split into cards);
  //   'exchange' an .lc-exchange block emitted by the post-processor for a
  //              ">>" paragraph (LOS7-2726): one card, never split.
  //
  // A chapter-OPENING exchange (one with no prose before it) is deliberately
  // NOT collected: it stays on the paper under the heading, which is the
  // documented v2 behaviour and what the Ikeshima/Wangi builds render today.
  // Only an exchange that follows prose is pulled into the sequence. Left in
  // the paper flow it ended up below every caption, out of narrative order,
  // because the sticky block is inserted before the first prose-block.
  // opts.stopAtClass ends the walk early at a marker element carrying that
  // class, in addition to the next h2 (used by the opening half of a
  // pause-and-resume chapter). opts.startAfterClass skips everything until
  // that marker, then collects to the next h2 (the resuming half). Neither
  // option is set for an ordinary, unsplit chapter, which behaves exactly as
  // before: the full h2-to-next-h2 range.
  function collectChapterItems(h2, opts) {
    opts = opts || {};
    var items = [];
    var seenProse = false;
    var collecting = !opts.startAfterClass;
    var sib = h2.nextElementSibling;
    while (sib) {
      if (sib.tagName && sib.tagName.toLowerCase() === 'h2') break;
      if (opts.stopAtClass && sib.classList && sib.classList.contains(opts.stopAtClass)) break;
      if (opts.startAfterClass && sib.classList && sib.classList.contains(opts.startAfterClass)) {
        collecting = true;
        sib = sib.nextElementSibling;
        continue;
      }
      if (collecting) {
        if (sib.classList && sib.classList.contains('prose-block')) {
          Array.prototype.forEach.call(
            sib.querySelectorAll('p'),
            function (p) { items.push({ node: p, kind: 'p' }); seenProse = true; }
          );
        } else if (sib.classList && sib.classList.contains('lc-exchange')) {
          if (seenProse) items.push({ node: sib, kind: 'exchange' });
        } else if (sib.tagName && sib.tagName.toLowerCase() === 'p') {
          items.push({ node: sib, kind: 'p' });
          seenProse = true;
        }
      }
      sib = sib.nextElementSibling;
    }
    return items;
  }

  // A split card under this many words is folded back into the card before
  // it (LOS7-3032). Three-sentence chunking left closing lines as cards of
  // their own ("Then he looked up."), a whole screen of scroll for five words.
  // Only within one paragraph, so the first card of every paragraph is kept,
  // and never a card a phase-table cue addresses (phaseCues above).
  var MIN_CARD_WORDS = 12;

  function sentenceSplit(text, chunkSize, protectedCues) {
    var marked = text.replace(/([.!?])\s+([A-Z])/g, '$1\x00$2');
    var sentences = marked.split('\x00').filter(function (s) { return s.trim(); });
    if (sentences.length <= chunkSize) return null;
    var chunks = [];
    for (var i = 0; i < sentences.length; i += chunkSize) {
      chunks.push(sentences.slice(i, i + chunkSize).join(' '));
    }
    var isCue = function (chunk) {
      var n = normCue(chunk);
      return (protectedCues || []).some(function (q) { return q && n.indexOf(q) === 0; });
    };
    for (var c = chunks.length - 1; c > 0; c--) {
      if (chunks[c].trim().split(/\s+/).length < MIN_CARD_WORDS && !isCue(chunks[c])) {
        chunks[c - 1] += ' ' + chunks[c];
        chunks.splice(c, 1);
      }
    }
    return chunks.length > 1 ? chunks : null;
  }

  // ----- Frame resolver (global, single source of truth) -------------------
  // ADR-0008. One resolver maps the current scroll position to exactly one
  // correct frame across ALL chapters, killing the paper-spill in both scroll
  // directions. The earlier per-block observer ("the last caption of THIS
  // block in view, update only on change") left a dead band between one
  // chapter's last caption and the next chapter's first caption, across that
  // gap no block's caption was in view, so the shared #lc-scrolly-img layer
  // held the PREVIOUS chapter's frame, which showed under the next heading in
  // the next chapter's 90vh reveal zone.
  //
  // Caption *visibility* is still pure CSS (scroll-driven opacity). The
  // resolver's only job is choosing which frame the fixed layer paints:
  //   1. the in-view caption nearest viewport centre (across every block) wins
  //      → its frame (the per-caption mapping below is unchanged);
  //   2. if NO caption is in view (a reveal or tail zone), fall back to the
  //      frame-0 of the block overlapping the viewport centre; this is the
  //      prime-on-enter behaviour that fills the reveal zone with the entering
  //      chapter's establishing frame instead of the previous chapter's;
  //   3. otherwise hold (a between-chapter gap, where paper sections cover the
  //      fixed layer so the held value is never visible anyway).
  // Driven by an IntersectionObserver over every block + caption (fires on each
  // boundary crossing, including momentum / programmatic scroll the scroll
  // event can miss) plus the existing rAF-throttled scroll listener.
  // Which crop to paint (LOS7-2794). #lc-scrolly-img is position:fixed and
  // viewport-filling, so the container's aspect IS the viewport's, and the crop
  // that should win is decided by ORIENTATION, not width.
  //
  // This was '(max-width: 749px)' at both call sites. A width test gets phones
  // right by coincidence and everything else wrong: a portrait tablet is 768px
  // wide, and a desktop window dragged tall and narrow is wider still, so both
  // were served the landscape crop, and cover then discarded more than half the
  // frame. A phone held sideways is the mirror image of the same bug.
  //
  // Declared HERE, at the scope wireFrameResolver and setupImageLayer share.
  // Both call it, and they are siblings, so a definition inside either one is
  // out of scope for the other - which is what happened on the first attempt:
  // `node --check` passed, because that is a syntax check and this is a scope
  // error, and the page threw ReferenceError at runtime instead.
  //
  // Frames with no portrait variant fall through to --bg-d as before, so this
  // is inert on every frame that has only one crop.
  function portraitViewport() {
    return window.matchMedia('(orientation: portrait)').matches;
  }

  function wireFrameResolver(scrollySections, creditsEnabled, motion) {
    var blocks = Array.prototype.slice.call(scrollySections).map(function (el) {
      return {
        el: el,
        bgs: Array.prototype.slice.call(el.querySelectorAll('.lc-scrolly__bg')),
        caps: Array.prototype.slice.call(el.querySelectorAll('.lc-scrolly__caption')),
        // data-frame-map (set in Liquid from lco.scrolly_intro_frames) picks how
        // this chapter's frames map to its captions:
        //   ''         source-paragraph index (data-scrolly-index), the default,
        //              unchanged, and what every article rendered before v2.
        //   'caption'  1:1 with caption cards in DOM order, so a paragraph that
        //              sentence-splits into several cards advances a frame each.
        //   'spread'   LOS7-2727: the frames are distributed evenly across the
        //              chapter's caption run by scroll position, so a chapter can
        //              hold MORE frames than it has captions (Ikeshima's island
        //              chapter: 44 prints over 19 caption cards). Both index
        //              modes above are capped at one frame per card.
        frameMap: el.getAttribute('data-frame-map') || '',
        // LOS7 scroll-linked map. Authored per chapter in
        // lco.scrolly_intro_frames and server-rendered onto the block. Compiled
        // lazily on first use, once the captions exist. null means "not a
        // phased chapter", which is every chapter but Ikeshima's the-wrong-port
        // and every other article.
        phasesRaw: el.getAttribute('data-phases') || '',
        phases: undefined
      };
    }).filter(function (b) { return b.bgs.length; });
    if (!blocks.length) return;

    // Map a caption to its frame element within its block (unchanged logic:
    // caption-order vs paragraph-index, with clamp-to-last so closing captions
    // hold the final image rather than wrapping back to frame 0).
    function frameElForCaption(bd, cap) {
      if (bd.bgs.length === 1) return bd.bgs[0];
      var pc = phasesFor(bd);
      if (pc) {
        var a = activePhase(bd);
        if (a) return phaseFrameEl(bd, a.phase, a.t);
      }
      if (bd.frameMap === 'spread') return spreadFrameEl(bd);
      var idx = bd.frameMap === 'caption' ? bd.caps.indexOf(cap)
                                          : parseInt(cap.dataset.scrollyIndex, 10);
      if (isNaN(idx) || idx < 0) idx = 0;
      if (idx > bd.bgs.length - 1) idx = bd.bgs.length - 1;
      return bd.bgs[idx] || bd.bgs[0];
    }

    // LOS7-2727 'spread'. Frame choice comes from where the viewport centre
    // sits in the chapter's caption RUN (first caption's top to last caption's
    // bottom), not from which caption is nearest centre, so the frame count is
    // free of the caption count: 44 prints advance smoothly across 19 cards.
    // A map frame is excluded: it is index 0 of a map chapter and belongs to
    // the reveal zone, which resolver rule 2 still serves. Monotonic in scroll
    // position, so it never jumps backwards mid-chapter.
    function spreadFrameEl(bd) {
      var pool = bd.spreadPool;
      if (!pool) {
        pool = bd.spreadPool = bd.bgs.filter(function (b) {
          return !b.hasAttribute('data-map');
        });
      }
      if (!pool.length) return bd.bgs[0];
      if (pool.length === 1 || !bd.caps.length) return pool[0];
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var centre = window.scrollY + vh / 2;
      var top = bd.caps[0].getBoundingClientRect().top + window.scrollY;
      var bot = bd.caps[bd.caps.length - 1].getBoundingClientRect().bottom + window.scrollY;
      // The end of the run has to be somewhere the viewport centre can actually
      // GET to. A last caption near the end of the document never reaches the
      // centre, because the page runs out of scroll first, and mapping to it
      // would leave the closing frames unreachable: the chapter would stop one
      // or two prints short of its last, every time.
      var end = Math.min(bot, document.body.scrollHeight - vh / 2);
      var span = end - top;
      if (!(span > 0)) return pool[0];
      var p = (centre - top) / span;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      var idx = Math.floor(p * pool.length);
      if (idx > pool.length - 1) idx = pool.length - 1;
      return pool[idx];
    }

    // The frame a block shows when none of its captions is in view. Frame 0
    // everywhere except the tail of a 'spread' chapter, where the sequence has
    // already run to its end and must stay there.
    function frameElNoCaption(bd, mid) {
      if (!bd.caps.length) return bd.bgs[0];
      var lastCap = bd.caps[bd.caps.length - 1];
      // Reveal zone: the captions are still below the midline, so the chapter
      // has not started. Open on frame 0 -- unchanged for every chapter.
      if (lastCap.getBoundingClientRect().bottom > mid) return bd.bgs[0];
      // Tail zone: every caption is above the midline, so the chapter is
      // FINISHED. Returning bgs[0] here put a chapter's OPENING image back on
      // screen after its own later frames had replaced it -- measured live at
      // 1600x1400 as sixty-years' 1961 aerial coming back to 31% of the
      // viewport at y41250, after the 1974 and 2023 plates had been the
      // subject. The 'spread' arm already made exactly this distinction
      // (LOS7-2798); it was never the spreading that needed it, it was the
      // tail, so every chapter gets it.
      if (bd.frameMap === 'spread') return spreadFrameEl(bd);
      // The frame the LAST CAPTION maps to, not the last frame in the DOM.
      // Under the default paragraph-index mapping a chapter can carry more
      // frames than it has paragraphs, and bgs[last] is then one the reader
      // never reached -- holding it into the gap would introduce a frame rather
      // than keep the one just seen. frameElForCaption answers for every
      // mapping mode, phased chapters included.
      var tailEl = frameElForCaption(bd, lastCap);
      // A phased chapter can close ON its map beat (the 5 PM ferry ends at
      // "At Konoura I thanked Yu", frame "map"). Past the block the map has
      // finished with the chapter, and resolving to it here sends update() down
      // the map branch, which pre-paints that chapter's FIRST photograph -- a
      // snap-back to its opening image. Measured live before this guard:
      // the-5-pm-ferry's chimney-and-plant back to 78% of the viewport at
      // y119750. Hold the chapter's LAST photograph instead.
      if (tailEl && tailEl.hasAttribute('data-map')) {
        for (var ti = bd.bgs.length - 1; ti >= 0; ti--) {
          if (!bd.bgs[ti].hasAttribute('data-map')) return bd.bgs[ti];
        }
      }
      return tailEl;
    }

    // ----- Scroll-linked phases (LOS7) ------------------------------------
    // A phased chapter is choreographed against caption CARDS, not paragraph
    // indices, and its map line is a continuous function of scroll rather than
    // a wall-clock animation.
    //
    // Cards are addressed by CUE TEXT, never by ordinal. data-scrolly-card is
    // dense and stable within a render, but it shifts the moment a paragraph is
    // edited or re-chunked, and a silently shifted ordinal shows the wrong
    // frame at the wrong sentence. The cue is the opening words of the caption
    // the beat belongs to, so the address survives editing and fails loudly.
    //
    // Compilation is FAIL-SHUT: if any cue misses, matches more than one card,
    // or resolves out of order, the whole table is discarded and the chapter
    // renders exactly as it does without one. Never half a story.
    function normText(t) {
      return String(t || '').replace(/\s+/g, ' ').trim().toLowerCase();
    }

    // A rejected table is silently replaced by the legacy whole-journey draw,
    // which looks like a choreography bug rather than a data one. Naming the
    // reason costs nothing in production and turns a silent fallback into a
    // one-line answer under ?lcdebug or the probe.
    function reject(why, detail) {
      if (!window.LC_SCROLLY_DEBUG) return;
      // eslint-disable-next-line no-console
      console.warn('[phases] table rejected: ' + why, detail);
    }

    function compilePhases(bd) {
      if (!bd.phasesRaw) return null;
      var cfg;
      try { cfg = JSON.parse(bd.phasesRaw); } catch (_e) { return null; }
      if (!cfg || !cfg.phases || !cfg.phases.length || !bd.caps.length) {
        reject('no phases in config, or no captions', bd.caps.length);
        return null;
      }
      var texts = bd.caps.map(function (c) { return normText(c.textContent); });
      var out = [];
      for (var i = 0; i < cfg.phases.length; i++) {
        var ph = cfg.phases[i];
        var cue = normText(ph.cue);
        if (!cue) return null;
        var hit = -1;
        for (var j = 0; j < texts.length; j++) {
          if (texts[j].indexOf(cue) === 0) {
            if (hit >= 0) {
              reject('ambiguous cue', cue);
              return null;
            }
            hit = j;
          }
        }
        if (hit < 0) {
          reject('cue matches no caption', cue);
          return null;
        }
        if (out.length && hit <= out[out.length - 1].card) {
          reject('cue is out of order', cue);
          return null;
        }
        out.push({
          card: hit,
          frame: ph.frame,
          grow: ph.grow || null,
          // Fraction of this beat's span before the line starts drawing, so a
          // pin can land before the route begins tracing out of it.
          growFrom: typeof ph.grow_from === 'number' ? ph.grow_from : 0,
          hold: ph.hold || [],
          dash: ph.dash || [],
          // Dashed legs that DRAW THEMSELVES IN across this beat rather than
          // being present from its first frame.
          dashGrow: ph.dash_grow || [],
          // Legs whose opacity runs 1 -> 0 across this beat, so a route the
          // story has finished with leaves the map instead of sitting on it.
          fade: ph.fade || [],
          // An authored frame this beat holds, fitted literally. Without it a
          // beat with no zoom falls back to the chapter's home frame, which
          // cannot hold a close-up across two captions.
          box: ph.box || null,
          // A beat can hand its frame over BEFORE it ends. The frame is
          // otherwise a property of the beat, so a photograph that has to be up
          // by the time a caption leaves cannot arrive until the NEXT beat's
          // caption reaches the middle of the screen, which on a long beat is
          // hundreds of pixels too late.
          frameTo: ph.frame_to !== undefined ? ph.frame_to : null,
          frameAt: typeof ph.frame_at === 'number' ? ph.frame_at : 1,
          // [start, end] as fractions of the beat for the fade, when the beat's
          // own span and its zoom both finish at the wrong moment for it.
          fadeAt: ph.fade_at && ph.fade_at.length === 2 ? ph.fade_at : null,
          // Fraction of a wiping beat's span at which the growing line
          // completes. Defaults to wipeFrom, which is where it used to be
          // pinned, and only differs when something has to happen between the
          // line landing and the paper rising.
          growUntil: typeof ph.grow_until === 'number' ? ph.grow_until : null,
          pins: ph.pins || [],
          // Pins that ENTER during this beat rather than being present from
          // its first frame. Everything else in `pins` is already at rest.
          pinEnter: ph.pin_enter || [],
          pinEnterSpan: typeof ph.pin_enter_span === 'number' ? ph.pin_enter_span : 0.12,
          // Measure the entrance against the chapter's APPROACH rather than the
          // beat's own progress. The first beat's t is pinned at 0 until its
          // caption reaches the middle of the screen, by which point the
          // caption has been on screen for hundreds of pixels, so an entrance
          // that has to be finished BEFORE the caption arrives cannot be
          // expressed against the beat at all.
          pinEnterOnApproach: !!ph.pin_enter_on_approach,
          // Where in the beat the entrance STARTS, so a pin can arrive as the
          // sentence reaches its name rather than as the caption appears.
          pinEnterFrom: typeof ph.pin_enter_from === 'number' ? ph.pin_enter_from : 0,
          zoomBox: ph.zoom && ph.zoom.box ? ph.zoom.box : null,
          // An authored frame the zoom starts FROM, so a beat can open on a
          // close-up and widen back out to the chapter's home frame. With no
          // box of its own that home frame is the target, which is how a
          // zoom-out is written: from only, no box.
          zoomFrom: ph.zoom && ph.zoom.from ? ph.zoom.from : null,
          zoomUntilCue: ph.zoom && ph.zoom.until_cue ? normText(ph.zoom.until_cue) : '',
          zoomUntilCard: -1,
          // [start, end] as fractions of THIS beat, for a camera move that has
          // to happen between two things inside one caption rather than across
          // two of them. Mutually exclusive with until_cue.
          zoomAt: ph.zoom && Array.isArray(ph.zoom.at) && ph.zoom.at.length === 2 ? ph.zoom.at : null,
          wipe: !!ph.wipe
        });
      }
      var k, m;
      // A zoom's finishing cue is resolved the same fail-shut way as a beat's
      // own: an authored caption that no longer matches discards the table
      // rather than leaving a zoom that never completes.
      for (k = 0; k < out.length; k++) {
        if (!out[k].zoomBox && !out[k].zoomFrom) continue;
        if (out[k].zoomAt) continue;
        if (!out[k].zoomUntilCue) { out[k].zoomUntilCard = -1; continue; }
        var zhit = -1;
        for (m = 0; m < texts.length; m++) {
          if (texts[m].indexOf(out[k].zoomUntilCue) === 0) {
            if (zhit >= 0) return null;
            zhit = m;
          }
        }
        if (zhit < 0 || zhit <= out[k].card) {
          reject('zoom until_cue is missing or not after its beat', out[k].zoomUntilCue);
          return null;
        }
        out[k].zoomUntilCard = zhit;
      }

      // Everything painted while each camera key is live, so the map can fit
      // the union of the authored box and the drawn set rather than the box
      // alone. 'home' owns every phase up to and including the wipe phase: the
      // wipe phase's line grows and its pins reveal BEFORE the cover reaches
      // half, while the camera is still on home. 'end' owns the wipe phase's
      // own draws, which its authored box already contains, so the union is a
      // no-op there and can only confirm it.
      var homeDraws = {};
      var endDraws = {};
      var ids;
      for (k = 0; k < out.length; k++) {
        ids = [].concat(out[k].pins || [], out[k].hold || [], out[k].dash || [], out[k].dashGrow || []);
        if (out[k].grow) ids.push(out[k].grow);
        for (m = 0; m < ids.length; m++) {
          homeDraws[ids[m]] = true;
          if (out[k].wipe) endDraws[ids[m]] = true;
        }
      }
      return {
        list: out,
        cameraHome: cfg.camera_home || null,
        cameraEnd: cfg.camera_end || null,
        cameraDrawsHome: Object.keys(homeDraws),
        cameraDrawsEnd: Object.keys(endDraws),
        wipeFrom: typeof cfg.wipe_from === 'number' ? cfg.wipe_from : 0.55
      };
    }

    function phasesFor(bd) {
      if (bd.phases === undefined) bd.phases = compilePhases(bd);
      return bd.phases;
    }

    // Extracted verbatim from spreadFrameEl so both paths share one geometry.
    // Deliberately keeps document.body.scrollHeight: swapping it for
    // documentElement would change the live 'spread' arithmetic.
    // Phases open on the reader's line, not the screen's middle (LOS7-3032).
    // A beat opens when its card's TOP edge reaches 75% of the viewport height,
    // where the eye meets a card coming up the screen; the centre line held
    // the old frame until the card was half read. Each beat's progress runs
    // from that point on its card to the same point on the next beat's card.
    var PHASE_LINE = 0.75;

    function phaseLineDoc() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return window.scrollY + vh * PHASE_LINE;
    }

    function cardTopDoc(bd, i) {
      var c = bd.caps[i];
      if (!c) return null;
      return c.getBoundingClientRect().top + window.scrollY;
    }

    function phaseProgress(topDoc, endDoc) {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var line = phaseLineDoc();
      // The furthest the line can travel: the page's end minus the quarter of
      // the viewport below it.
      var end = Math.min(endDoc, document.body.scrollHeight - vh * (1 - PHASE_LINE));
      var span = end - topDoc;
      if (!(span > 0)) return line >= topDoc ? 1 : 0;
      var p = (line - topDoc) / span;
      return p < 0 ? 0 : (p > 1 ? 1 : p);
    }

    // Centre-line span, kept for the pin approach, which is tied to the frame
    // resolver making the map active and so to the resolver's own centre line.
    function spanProgress(topDoc, endDoc) {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var centre = window.scrollY + vh / 2;
      var end = Math.min(endDoc, document.body.scrollHeight - vh / 2);
      var span = end - topDoc;
      if (!(span > 0)) return centre >= topDoc ? 1 : 0;
      var p = (centre - topDoc) / span;
      return p < 0 ? 0 : (p > 1 ? 1 : p);
    }

    function cardCentreDoc(bd, i) {
      var c = bd.caps[i];
      if (!c) return null;
      var r = c.getBoundingClientRect();
      return r.top + window.scrollY + r.height / 2;
    }

    // Which phase owns the current scroll position, and how far through it.
    function activePhase(bd) {
      var pc = phasesFor(bd);
      if (!pc) return null;
      var line = phaseLineDoc();
      var idx = -1;
      for (var i = 0; i < pc.list.length; i++) {
        var ct = cardTopDoc(bd, pc.list[i].card);
        if (ct === null) return null;
        // A beat opens AS its caption's top reaches the phase line. Comparing
        // exactly leaves the phase one tick late at the boundary, which shows
        // the outgoing frame on the very caption the beat belongs to. The 2px
        // tolerance absorbs sub-pixel rounding only.
        if (ct <= line + 2) idx = i; else break;
      }
      if (idx < 0) idx = 0;
      var startCard = cardTopDoc(bd, pc.list[idx].card);
      var nextPhase = pc.list[idx + 1];
      var endDoc = nextPhase
        ? cardTopDoc(bd, nextPhase.card)
        : bd.el.getBoundingClientRect().bottom + window.scrollY;
      return { cfg: pc, i: idx, phase: pc.list[idx], t: phaseProgress(startCard, endDoc) };
    }

    function phaseFrameEl(bd, phase, t) {
      var frame = phase.frame;
      if (phase.frameTo !== null && typeof t === 'number' && t >= phase.frameAt) {
        frame = phase.frameTo;
      }
      if (frame === 'map') {
        for (var i = 0; i < bd.bgs.length; i++) {
          if (bd.bgs[i].hasAttribute('data-map')) return bd.bgs[i];
        }
        return bd.bgs[0];
      }
      var n = parseInt(frame, 10);
      if (isNaN(n) || n < 0) n = 0;
      if (n > bd.bgs.length - 1) n = bd.bgs.length - 1;
      return bd.bgs[n];
    }

    // Publish the line fraction, held legs, pins, camera and wipe on EVERY
    // tick, not only when the painted element changes: growth is continuous.
    function pumpPhase(bd) {
      var a = activePhase(bd);
      if (!a) return;
      var ph = a.phase;
      // The frame the beat is ACTUALLY showing right now, which is not ph.frame
      // once the beat has handed over. The resolver and this function have to
      // agree: if one says photograph and the other says map, the map is told
      // it is visible while the photograph is painted over it and the two
      // fight on every tick.
      var shownFrame = ph.frameTo !== null && a.t >= ph.frameAt ? ph.frameTo : ph.frame;
      var onMap = shownFrame === 'map';
      var t = a.t;
      var cover = 0;
      // 'home' is live for every beat before the wipe hands over to 'end',
      // not just the first. Keying it to phase 0 meant a reader who landed or
      // reloaded deep in the chapter never fitted the camera at all and got
      // the map's initial zoom 9 centred on the payload centroid. drawPhase's
      // lastCameraKey guard still fires each key exactly once.
      var camera = 'home';
      var cameraBox = a.cfg.cameraHome;
      var cameraDraws = a.cfg.cameraDrawsHome;
      if (ph.box) {
        // Keyed per beat so it fires once on entry, exactly like 'home', and
        // fitted literally: an authored close-up must not be widened out to
        // cover the whole journey.
        camera = 'box:' + ph.card;
        cameraBox = ph.box;
        cameraDraws = null;
      }

      // Pin entrance. A pin named in pinEnter fades and grows in across the
      // first pinEnterSpan of its beat; every other pin in `pins` is already at
      // rest. Scroll-driven, so scrolling back takes the pin out again.
      var pinAlpha = {};
      var pi;
      for (pi = 0; pi < (ph.pins || []).length; pi++) pinAlpha[ph.pins[pi]] = 1;
      // The approach runs from the viewport centre crossing the block's top,
      // which is both the moment the paper finishes lifting off the map and the
      // moment the frame resolver makes the map active, to the cue caption's
      // centre. Same span the camera's own until_cue zoom is measured against,
      // so a pin can be at rest before its caption is readable.
      var pinT = a.t;
      if (ph.pinEnterOnApproach) {
        var cueCentre = cardCentreDoc(bd, ph.card);
        if (cueCentre !== null) {
          pinT = spanProgress(bd.el.getBoundingClientRect().top + window.scrollY, cueCentre);
        }
      }
      for (pi = 0; pi < (ph.pinEnter || []).length; pi++) {
        var span = ph.pinEnterSpan > 0 ? ph.pinEnterSpan : 0.12;
        var from = ph.pinEnterFrom || 0;
        pinAlpha[ph.pinEnter[pi]] = Math.max(0, Math.min(1, (pinT - from) / span));
      }

      // The line waits for the pin it grows out of.
      if (ph.grow && ph.growFrom > 0) {
        t = a.t <= ph.growFrom ? 0 : (a.t - ph.growFrom) / (1 - ph.growFrom);
      }

      // A line can be required to LAND before its beat ends, so whatever comes
      // next in the beat gets the rest of the scroll to itself. grow_until used
      // to be read only on a WIPING beat, where it was measured against the
      // paper's start; a beat that hands over to the document's own paper
      // instead never wipes, and without this its line drew all the way to the
      // last pixel of the beat, still growing under the camera move that was
      // supposed to follow it.
      if (!ph.wipe && ph.growUntil > 0) {
        t = Math.min(1, t / ph.growUntil);
      }

      // Scroll-linked zoom: from the frame the chapter opened on to an
      // authored one, finishing as the named caption leaves the middle of the
      // screen. Runs on the beat's own span measured against that card, not on
      // a timer, so it tracks the reader in both directions.
      var lerp = null;
      if ((ph.zoomBox || ph.zoomFrom) && a.cfg.cameraHome) {
        var zt = null;
        if (ph.zoomAt) {
          // Driven by this beat's own progress, so a camera move can sit
          // between the line landing and the paper rising.
          var z0 = ph.zoomAt[0];
          var z1 = ph.zoomAt[1];
          zt = z1 > z0 ? (a.t - z0) / (z1 - z0) : (a.t >= z1 ? 1 : 0);
          zt = zt < 0 ? 0 : (zt > 1 ? 1 : zt);
        } else {
          var startY = cardTopDoc(bd, ph.card);
          var endY = ph.zoomUntilCard >= 0
            ? cardTopDoc(bd, ph.zoomUntilCard)
            : null;
          if (startY !== null && endY !== null) zt = phaseProgress(startY, endY);
        }
        if (zt !== null) {
          // Either end may be the chapter's home frame, and that end has to
          // carry the same draws the 'home' camera key fits against or the
          // frame jumps as the zoom starts and again as it hands back.
          lerp = {
            key: 'zoom:' + ph.card,
            from: ph.zoomFrom || a.cfg.cameraHome,
            fromDraws: ph.zoomFrom ? null : a.cfg.cameraDrawsHome,
            to: ph.zoomBox || a.cfg.cameraHome,
            toDraws: ph.zoomBox ? null : a.cfg.cameraDrawsHome,
            t: zt
          };
        }
      }

      if (ph.wipe) {
        // The line finishes before the paper starts, so the camera can only
        // move once the route has landed and the map is fully covered. The
        // line's own deadline is separate from the paper's start, so a beat can
        // land the route, move the camera where the reader can see it, and only
        // then bring the paper up.
        var wf = a.cfg.wipeFrom;
        var gu = typeof ph.growUntil === 'number' ? ph.growUntil : wf;
        t = gu > 0 ? Math.min(1, a.t / gu) : 1;
        // The paper travels one way: up from below the fold, across the map,
        // and off the top. Half way it is covering, which is the only moment
        // the camera is allowed to move.
        cover = a.t <= wf ? 0 : (a.t - wf) / (1 - wf);
        if (cover >= 0.5) {
          camera = 'end';
          cameraBox = a.cfg.cameraEnd;
          // Fitted LITERALLY. The union was written when camera_end contained
          // everything drawn under it, where it was a no-op; once the end frame
          // is a deliberate close-up the union silently widens back out of it
          // and the frame jerks open behind a half-raised sheet of paper.
          cameraDraws = null;
          // drawPhase gives a live lerp priority over every keyed move, so the
          // beat's own zoom has to stand down once the paper has taken over.
          lerp = null;
        }
      }

      // A leg named in `fade` runs 1 -> 0 across the beat, tracking the zoom
      // when there is one so the route leaves as the frame closes in, and the
      // beat's own span otherwise.
      var lineAlpha = {};
      var fadeT = lerp ? lerp.t : a.t;
      if (ph.fadeAt) {
        // An authored window wins over both. A route that has to be gone by the
        // halfway point of its caption cannot be tied to a zoom that finishes
        // later, and tying it to the beat leaves it on screen to the last pixel.
        var f0 = ph.fadeAt[0];
        var f1 = ph.fadeAt[1];
        fadeT = f1 > f0 ? (a.t - f0) / (f1 - f0) : a.t >= f1 ? 1 : 0;
      }
      for (var fi = 0; fi < (ph.fade || []).length; fi++) {
        lineAlpha[ph.fade[fi]] = 1 - Math.max(0, Math.min(1, fadeT));
      }
      // Dashed routes draw themselves in across the beat.
      var dashT = (ph.dashGrow && ph.dashGrow.length) ? Math.max(0, Math.min(1, a.t)) : 1;

      if (window.LC_SCROLLY_DEBUG) {
        window.__lcActive = {
          i: a.i,
          cards: a.cfg.list.map(function (x) { return x.card; }).join(','),
          frame: ph.frame,
          t: Math.round(a.t * 100) / 100
        };
      }
      setCover(bd, ph.wipe ? cover : 0);

      if (window.LCJourneyMap && window.LCJourneyMap.applyPhase) {
        window.LCJourneyMap.applyPhase({
          anchor: bd.el.getAttribute('data-anchor') || '',
          visible: onMap,
          t: t,
          grow: ph.grow,
          hold: ph.hold,
          dash: ph.dash,
          dashGrow: ph.dashGrow,
          dashT: dashT,
          lineAlpha: lineAlpha,
          pins: ph.pins,
          camera: camera,
          cameraBox: cameraBox,
          cameraDraws: cameraDraws,
          cameraLerp: lerp,
          pinAlpha: pinAlpha
        });
      }
    }

    // The cover is an empty div, and assets/base.css carries
    // div:empty { display: none }, so it has never painted. The rule that
    // reveals it is scoped to [data-phases] and carries the specificity to beat
    // that. Driven straight from scroll position: 0 is clear, 1 fully covering.
    function setCover(bd, c) {
      var cover = bd.coverEl;
      if (cover === undefined) {
        cover = bd.coverEl = bd.el.querySelector('.lc-scrolly__cover') || null;
      }
      if (!cover) return;
      // c 0 -> just below the fold, 0.5 -> covering, 1 -> clear off the top.
      cover.style.transform = 'translate3d(0,' + (100 - c * 200).toFixed(2) + '%,0)';
      cover.setAttribute('data-cover', c > 0 && c < 1 ? '1' : '0');
    }

    function resolveFrameEl() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var mid = vh / 2;
      // 1. caption nearest viewport centre among captions in view, all blocks
      var bestCap = null, bestBd = null, bestDist = Infinity;
      for (var i = 0; i < blocks.length; i++) {
        var caps = blocks[i].caps;
        for (var j = 0; j < caps.length; j++) {
          var r = caps[j].getBoundingClientRect();
          if (r.bottom > 0 && r.top < vh) {
            var d = Math.abs((r.top + r.bottom) / 2 - mid);
            if (d < bestDist) { bestDist = d; bestCap = caps[j]; bestBd = blocks[i]; }
          }
        }
      }
      if (bestCap) return { el: frameElForCaption(bestBd, bestCap), bd: bestBd };
      // 2. reveal / tail zone: frame-0 of the block over the viewport centre.
      //    A 'spread' chapter holds its LAST frame in the tail zone instead of
      //    snapping back to frame 0 (LOS7-2727); its reveal zone still opens on
      //    frame 0, which on a map chapter is the map.
      for (var k = 0; k < blocks.length; k++) {
        var br = blocks[k].el.getBoundingClientRect();
        if (br.top <= mid && br.bottom >= mid) {
          return { el: frameElNoCaption(blocks[k], mid), bd: blocks[k] };
        }
      }
      // 3. between-chapter gap (paper-covered): the nearest block's frame,
      //    through frameElNoCaption rather than bgs[0] directly (LOS7-2798).
      //    Rule 2 above already says a 'spread' chapter holds its LAST frame in
      //    the tail zone rather than snapping back to frame 0; this rule took
      //    frame 0 unconditionally. The gap is wide - measured on the live
      //    article, over-the-gate's last caption ended at 66284 and
      //    the-5-pm-ferry's first began at 68603, so 2,319px had no caption in
      //    view and the midline sat outside both section boxes. Landing there at
      //    the end of a spread chapter snapped the sticky back to that chapter's
      //    opening frame: Ikeshima's coastal stairway faded up again under the
      //    5 PM ferry heading, 27,000px after it was last the subject.
      //
      //    frameElNoCaption keeps the reveal zone opening on frame 0 and is
      //    identical to bgs[0] for every non-spread block.
      //
      //    Measured on the live article at 1440x900, main vs this change with
      //    BOTH builds served from the working tree so the comparison isolates
      //    the code: control main-vs-main 0 differing samples of 247, and
      //    main-vs-fix exactly ONE -- y=63960, the stairway replaced by
      //    over-the-gate's own closing frame. Every chapter still opens on its
      //    own first frame, pond-island included (its 1977 oblique-aerial
      //    plate). An earlier pass recorded this as also changing pond-island's
      //    opening and parked the fix for it; that did not reproduce against a
      //    clean same-build control, and the instrument note on LOS7-2798
      //    already flagged an over-reporting artefact from too short a settle.
      // LOS7-2944. This gap is paper-covered, so whatever the photograph layer
      // holds here is the image the paper LIFTS ONTO. Resolving to the NEAREST
      // block hands back the OUTGOING chapter's frame 0 (frameElNoCaption
      // returns bgs[0] for every non-spread block), so a chapter's opening image
      // faded up again thousands of px after its own later frames had replaced
      // it. Measured live at 1600x1400: sixty-years' 1961 aerial held across the
      // whole 8,733px island gap, and the-control-room's console frame across
      // 8,138px, both after later frames of those chapters had been on screen.
      //
      // So resolve FORWARD -- the block the reader is heading INTO, whose frame
      // 0 is the image the paper is about to reveal. Chosen by smallest positive
      // (top - mid) rather than array position, so it does not depend on the
      // blocks having been relocated in document order.
      //
      // Past the last block there is nothing ahead, so hold that block's LAST
      // frame rather than snapping back to its first.
      var ahead = null, ad = Infinity;
      for (var m = 0; m < blocks.length; m++) {
        var rr = blocks[m].el.getBoundingClientRect();
        if (rr.top >= mid && rr.top - mid < ad) { ad = rr.top - mid; ahead = blocks[m]; }
      }
      if (ahead) return { el: frameElNoCaption(ahead, mid), bd: ahead, near: true };
      var behind = null, bdist = Infinity;
      for (var m2 = 0; m2 < blocks.length; m2++) {
        var rb = blocks[m2].el.getBoundingClientRect();
        if (rb.bottom <= mid && mid - rb.bottom < bdist) { bdist = mid - rb.bottom; behind = blocks[m2]; }
      }
      // Through frameElNoCaption, exactly like the forward case: past the last
      // block its tail-zone arm answers, holding the frame that block's last
      // caption mapped to rather than its first (or a frame the captions never
      // reached).
      return behind
        ? { el: frameElNoCaption(behind, mid), bd: behind, near: true }
        : null;
    }

    // Whether the frame we are LEAVING was the map. Probing the DOM for
    // .is-active cannot answer this: pumpPhase drives the map's visibility from
    // the same scroll tick (it passes `visible: onMap`), so by the time
    // paintFrame runs the class may already be gone and the handover would be
    // read as an ordinary photograph-to-photograph swap.
    var lastFrameWasMap = false;

    function paintFrame(bgEl) {
      var imgLayer = document.getElementById('lc-scrolly-img');
      if (!imgLayer || !bgEl) return;
      // Map frame (LOS7-2659): index 0 of a journey-map chapter carries no
      // image. Hand the chapter to lc-journey-map.js, which shows its fixed
      // map layer over the photograph layer, and clear the credit overlay.
      // The last photograph stays painted underneath so the map can fade.
      if (bgEl.hasAttribute('data-map')) {
        var mapBlock = bgEl.closest ? bgEl.closest('.lc-scrolly') : null;
        var mapAnchor = mapBlock ? (mapBlock.getAttribute('data-anchor') || '') : '';
        if (window.LCJourneyMap && mapAnchor) window.LCJourneyMap.show(mapAnchor);
        if (imgLayer.__setImage) imgLayer.__setImage(null, '', '');
        lastFrameWasMap = true;
        return;
      }
      // The journey map paints ABOVE the photograph layer, and that layer is
      // still holding whatever was last painted on it, which after a chapter or
      // two is a photograph from somewhere else entirely. Hiding the map and
      // swapping the photograph at the same time runs two 320ms fades against
      // each other, and halfway through BOTH are half transparent, so the stale
      // frame underneath shows through the gap. That is what reads as the
      // picture shifting between the map and the photograph.
      //
      // So: swap the photograph FIRST and instantly, while the map is still
      // opaque and covering it, and only then dissolve the map off it. One
      // animation instead of two, and nothing underneath is ever revealed.
      var mapUp = lastFrameWasMap;
      lastFrameWasMap = false;
      // Backgrounds are stored as CSS custom properties (--bg-d / --bg-m) inline
      // on .lc-scrolly__bg via Liquid; --bg-m is the portrait crop. The visible
      // paint is the fixed #lc-scrolly-img layer.
      var bgUrl = (portraitViewport() && bgEl.style.getPropertyValue('--bg-m')) ||
                  bgEl.style.getPropertyValue('--bg-d');
      if (!bgUrl) return;
      var credit = creditsEnabled ? (bgEl.getAttribute('data-credit') || '') : '';
      // Film marker (LOS7-2659): the quiet camera line for film frames.
      var film = creditsEnabled ? (bgEl.getAttribute('data-film-line') || '') : '';
      // Framing (LOS7-2728): optional per-frame overrides of the cover crop.
      // data-fit="contain" paints the whole frame on the paper ground (a 2:3
      // print on a landscape viewport keeps its feet); --focal moves the cover
      // crop's anchor ("50% 20%"). Both default to today's cover/centre.
      var fit = bgEl.getAttribute('data-fit') || '';
      var focal = (bgEl.style.getPropertyValue('--focal') || '').trim();
      if (imgLayer.__setImage) imgLayer.__setImage(bgUrl, credit, film, fit, focal, mapUp);
      else imgLayer.style.backgroundImage = bgUrl; // pre-crossfade fallback
      // Only now, with the new photograph already in place underneath it.
      if (window.LCJourneyMap) window.LCJourneyMap.hide();
    }

    // Chapter announcement (LOS7-2659): the chapter rail (lc-article-toc.js
    // scrolly mode) and anything else that needs "which chapter is the
    // resolver on" listens for this rather than running its own observer.
    function announceChapter(bd) {
      if (!bd || !bd.el || typeof window.CustomEvent !== 'function') return;
      var anchor = bd.el.getAttribute('data-anchor') || '';
      if (!anchor) return;
      document.dispatchEvent(new window.CustomEvent('lc:scrolly-chapter', { detail: { anchor: anchor } }));
    }

    var lastEl = null;
    var lastBd = null;
    // The map frame most recently handled by the near (paper-covered) branch,
    // so that branch runs once per boundary instead of on every scroll tick.
    var lastNearMap = null;
    function update() {
      var r = resolveFrameEl();
      if (!r) return;
      // A between-chapter (paper-covered) resolution still pre-paints the
      // nearest photograph, but never shows the map (that would load Mapbox
      // on page load for every reader) and never counts as being IN that
      // chapter for the rail.
      // LOS7-2944. A between-chapter resolution landing on a map frame used to
      // paint NOTHING and return, so the photograph layer kept the PREVIOUS
      // chapter's frame for the whole of the map chapter's run, and the map then
      // dissolved off it onto that stale image. Measured live: One day's drone
      // frame held across both map boundaries, ~19,000px after it was the
      // subject.
      //
      // Pre-paint the map chapter's OWN first photograph instead, so the paper
      // lifts onto its image and the map fades off its image. A map-only chapter
      // has no photograph of its own, so CLEAR the layer rather than leave
      // someone else's frame under the map.
      //
      // The map itself is still never shown here. LCJourneyMap.show() is what
      // loads Mapbox, and a near resolution fires at scroll 0, so calling it
      // would pull Mapbox onto page load for every reader. That intent is why
      // the early return existed and it is preserved.
      if (r.near && r.el && r.el.hasAttribute('data-map')) {
        if (lastNearMap !== r.el) {
          lastNearMap = r.el;
          var ownPhoto = null, q;
          if (r.bd && r.bd.bgs) {
            for (q = 0; q < r.bd.bgs.length; q++) {
              if (!r.bd.bgs[q].hasAttribute('data-map')) { ownPhoto = r.bd.bgs[q]; break; }
            }
          }
          if (ownPhoto) {
            if (ownPhoto !== lastEl) { lastEl = ownPhoto; paintFrame(ownPhoto); }
          } else if (lastBd) {
            // A map-ONLY chapter has no photograph to pre-paint, so the paper
            // used to lift onto whatever the last chapter left behind: measured
            // live, One day's drone frame came back to 59% of the viewport at
            // y10000 before the map arrived at y10250. Show the chapter's own
            // map here instead, so the paper lifts onto the map itself.
            //
            // Gated on lastBd, which is only set once the reader has actually
            // been INSIDE a scrolly chapter. That is what keeps the original
            // intent: at page load nothing has been entered, so Mapbox is still
            // never pulled in for a reader who has not started scrolling.
            if (r.el !== lastEl) {
              lastEl = r.el;
              paintFrame(r.el);
              // The map is now covering, so take the previous chapter's
              // photograph off the layer underneath it rather than leaving it
              // to be uncovered when the map dissolves.
              var imgC = document.getElementById('lc-scrolly-img');
              if (imgC && imgC.__clearImage) imgC.__clearImage();
            }
          } else {
            lastEl = null;
          }
        }
        return;
      }
      lastNearMap = null;
      // Phase state is continuous, so it is pumped every tick regardless of
      // whether the painted element changed.
      if (r.bd && phasesFor(r.bd) && !r.near) pumpPhase(r.bd);
      if (r.el && r.el !== lastEl) { lastEl = r.el; paintFrame(r.el); }
      if (!r.near && r.bd && r.bd !== lastBd) { lastBd = r.bd; announceChapter(r.bd); }
    }

    // ----- Settle and pull-back (2026-09-26 motion, items C and D) ----------
    // Both are pure functions of scroll position, written on every tick after
    // the resolver, the same doctrine as setCover: scrolling back reverses
    // them exactly and nothing is left running on a timer.
    //
    // Settle: as the paper above a chapter lifts off, the photograph goes from
    // 1.06 to 1.0. It is ONE custom property on #lc-scrolly-img that both
    // crossfade layers read, so an incoming and an outgoing layer can never
    // disagree about it mid-fade. The credit overlay and the journey map are
    // not layers, so neither is ever scaled.
    //
    // Pull-back: a frame carrying data-pullback (authored as "pullback": true
    // on its lco.photographs entry) runs 1.2 to 1.0 from the moment it is the
    // frame on screen to the moment it stops being it, or the paper after the
    // chapter starts to rise over it, whichever comes first. It is set on the
    // layer holding THAT image only, and replaces the settle there.
    var SETTLE_FROM = 1.06;
    var PULL_FROM = 1.2;
    var motionLayer = motion ? document.getElementById('lc-scrolly-img') : null;
    if (motionLayer) motionLayer.classList.add('lc-motion');
    var pullFrames = [];
    if (motionLayer) {
      blocks.forEach(function (bd) {
        bd.bgs.forEach(function (bg) {
          if (bg.hasAttribute('data-pullback') && !bg.hasAttribute('data-map')) {
            pullFrames.push({ bd: bd, el: bg });
          }
        });
      });
    }

    function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

    function blockBoxDoc(bd) {
      var r = bd.el.getBoundingClientRect();
      return { top: r.top + window.scrollY, bottom: r.bottom + window.scrollY };
    }

    // The scrollY at which the resolver first puts `bg` on screen, and the
    // scrollY at which it hands over to another frame (null if it never does
    // within the chapter). Answered per mapping mode from the same geometry
    // the resolver itself uses, so the two cannot drift apart.
    function frameSpanY(bd, bg) {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var pc = phasesFor(bd);
      var i, j;
      if (pc) {
        for (i = 0; i < pc.list.length; i++) {
          var ph = pc.list[i];
          var top = cardTopDoc(bd, ph.card);
          var next = pc.list[i + 1] ? cardTopDoc(bd, pc.list[i + 1].card) : null;
          if (top === null) return null;
          var endDoc = next !== null ? next : blockBoxDoc(bd).bottom;
          var startDoc = null;
          var stopDoc = next;
          if (phaseFrameEl(bd, ph, 0) === bg) {
            startDoc = top;
            if (ph.frameTo !== null && phaseFrameEl(bd, ph, 1) !== bg) {
              stopDoc = top + (endDoc - top) * ph.frameAt;
            }
          } else if (ph.frameTo !== null && phaseFrameEl(bd, ph, 1) === bg) {
            startDoc = top + (endDoc - top) * ph.frameAt;
          }
          if (startDoc !== null) {
            // Carry on through any later beats that keep showing it.
            for (j = i + 1; j < pc.list.length && stopDoc !== null; j++) {
              if (phaseFrameEl(bd, pc.list[j], 0) !== bg) break;
              stopDoc = pc.list[j + 1] ? cardTopDoc(bd, pc.list[j + 1].card) : null;
            }
            return {
              start: startDoc - vh * PHASE_LINE,
              stop: stopDoc === null ? null : stopDoc - vh * PHASE_LINE
            };
          }
        }
        return null;
      }
      if (bd.frameMap === 'spread') {
        var pool = bd.spreadPool || bd.bgs.filter(function (b) { return !b.hasAttribute('data-map'); });
        var k = pool.indexOf(bg);
        if (k < 0 || !bd.caps.length) return null;
        var runTop = bd.caps[0].getBoundingClientRect().top + window.scrollY;
        var runBot = bd.caps[bd.caps.length - 1].getBoundingClientRect().bottom + window.scrollY;
        var runEnd = Math.min(runBot, document.body.scrollHeight - vh / 2);
        var span = runEnd - runTop;
        if (!(span > 0)) return null;
        return {
          start: runTop + span * (k / pool.length) - vh / 2,
          stop: k < pool.length - 1 ? runTop + span * ((k + 1) / pool.length) - vh / 2 : null
        };
      }
      // Index mappings: the frame is on screen from the first caption that
      // maps to it becoming the caption nearest the viewport centre. The
      // nearest caption changes halfway between two captions' centres.
      var first = -1, last = -1;
      for (i = 0; i < bd.caps.length; i++) {
        if (frameElForCaption(bd, bd.caps[i]) === bg) {
          if (first < 0) first = i;
          last = i;
        }
      }
      if (first < 0) return null;
      function centreDoc(n) {
        var r = bd.caps[n].getBoundingClientRect();
        return r.top + window.scrollY + r.height / 2;
      }
      var s = first > 0 ? (centreDoc(first - 1) + centreDoc(first)) / 2 : centreDoc(first) - vh / 2;
      var e = last < bd.caps.length - 1 ? (centreDoc(last) + centreDoc(last + 1)) / 2 : null;
      return { start: s - vh / 2, stop: e === null ? null : e - vh / 2 };
    }

    function pullScale(pf) {
      var span = frameSpanY(pf.bd, pf.el);
      if (!span) return 1;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // The paper after the chapter starts to rise once the block's bottom
      // edge reaches the bottom of the viewport.
      var paperY = blockBoxDoc(pf.bd).bottom - vh;
      var end = span.stop === null ? paperY : Math.min(span.stop, paperY);
      var t = end > span.start ? (window.scrollY - span.start) / (end - span.start)
                               : (window.scrollY >= span.start ? 1 : 0);
      return PULL_FROM - (PULL_FROM - 1) * easeOutCubic(clamp01(t));
    }

    function settleScale() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // The chapter being read, or the next one if the reader is on paper:
      // the first block, in document order, whose bottom is still below the
      // top of the viewport. The paper above it ends at its top edge.
      var target = null, targetTop = Infinity;
      for (var i = 0; i < blocks.length; i++) {
        var r = blocks[i].el.getBoundingClientRect();
        if (r.bottom > 0 && r.top < targetTop) { target = blocks[i]; targetTop = r.top; }
      }
      if (!target) return 1;
      var p = clamp01(1 - targetTop / vh);
      return SETTLE_FROM - (SETTLE_FROM - 1) * easeOutCubic(p);
    }

    function paintMotion() {
      if (!motionLayer) return;
      motionLayer.style.setProperty('--lc-settle', settleScale().toFixed(4));
      if (!motionLayer.__setPullback) return;
      var pulls = {};
      for (var i = 0; i < pullFrames.length; i++) {
        var el = pullFrames[i].el;
        var url = (portraitViewport() && el.style.getPropertyValue('--bg-m')) ||
                  el.style.getPropertyValue('--bg-d');
        if (url) pulls[url] = pullScale(pullFrames[i]).toFixed(4);
      }
      motionLayer.__setPullback(pulls);
    }

    var rafPending = false;
    function onScroll() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () { rafPending = false; update(); paintMotion(); });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { update(); paintMotion(); }, { passive: true });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function () { update(); },
        { threshold: [0, 0.01, 0.5, 0.99, 1] });
      blocks.forEach(function (bd) {
        io.observe(bd.el);
        bd.caps.forEach(function (c) { io.observe(c); });
      });
    }

    update();
    paintMotion();
  }

  // ----- Image layer + paper sections (interleave mode) ---------------------
  function setupImageLayer(sections, creditsEnabled) {
    var imgLayer = document.getElementById('lc-scrolly-img');
    if (!imgLayer) {
      imgLayer = document.createElement('div');
      imgLayer.id = 'lc-scrolly-img';
      document.body.appendChild(imgLayer);
    }

    // Two stacked layers so a frame change is a true crossfade. background-image
    // can't be transitioned, hence the A/B pair. ADR-0008: the incoming layer
    // is promoted ABOVE the outgoing one and fades in over it while the outgoing
    // stays fully opaque underneath, so composite coverage is always 100%, so the
    // paper background-color of #lc-scrolly-img is never exposed mid-fade (the
    // old "paper trough", where both layers sat near 0.5 opacity at the
    // crossover, is gone).
    var layers = Array.prototype.slice.call(
      imgLayer.querySelectorAll('.lc-scrolly-img__layer')
    );
    if (layers.length < 2) {
      imgLayer.textContent = '';
      var a = document.createElement('div');
      var b = document.createElement('div');
      a.className = 'lc-scrolly-img__layer';
      b.className = 'lc-scrolly-img__layer';
      imgLayer.appendChild(a);
      imgLayer.appendChild(b);
      layers = [a, b];
    }

    // Dormant until the first chapter is near (LOS7-3032 perf audit). The
    // resolver paints a frame into this fixed layer at load, UNDER the hero:
    // an early 200KB photograph competing with the LCP poster, and on wide
    // screens it out-sized the poster and became the reported LCP at ~4.5s.
    // While dormant the layer is hidden and its layers carry no background
    // image (lc-scrollytelling.css), so nothing is fetched or painted.
    var firstBlock = null;
    (sections || []).forEach(function (sec) {
      var el = sec && (sec.el || sec);
      if (!el || !el.getBoundingClientRect) return;
      if (!firstBlock || el.getBoundingClientRect().top < firstBlock.getBoundingClientRect().top) firstBlock = el;
    });
    if (firstBlock && 'IntersectionObserver' in window &&
        firstBlock.getBoundingClientRect().top > window.innerHeight * 2) {
      imgLayer.classList.add('is-dormant');
      var wake = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        wake.disconnect();
        imgLayer.classList.remove('is-dormant');
        // One viewport ahead: at 2560x1440 One day starts 2.45 viewports down,
        // and a 150% margin woke the layer at load, so it still took LCP.
      }, { rootMargin: '100% 0px 100% 0px', threshold: 0 });
      wake.observe(firstBlock);
    }

    // Real-text credit overlay (aria-hidden, because the credit is also folded into
    // each frame's aria-label, the accessible source, so the overlay must not
    // double-announce). Fixed bottom corner over a scrim; shown only when the
    // active frame carries a credit and credits are enabled for this section.
    var creditEl = null;
    if (creditsEnabled) {
      creditEl = imgLayer.querySelector('.lc-scrolly-img__credit');
      if (!creditEl) {
        creditEl = document.createElement('div');
        creditEl.className = 'lc-scrolly-img__credit';
        creditEl.setAttribute('aria-hidden', 'true');
        imgLayer.appendChild(creditEl);
      }
    }
    // Two lines in one box: the attribution credit and, for film frames
    // (LOS7-2659), the quiet camera line ("Nikon F100 · Kodak Portra 400").
    // Either alone shows the box; neither hides it.
    // The two lines are built ONCE and then only have their text set. The
    // previous version emptied the element and re-created both spans on every
    // photograph change, which is most captions in a chapter with a frame per
    // caption. That matters more than it looks: the credit carries
    // backdrop-filter, so tearing its children down and rebuilding them forces
    // the frosted backdrop layer to be re-rasterised, and Dawn's base.css
    // `div:empty { display: none }` matches the element for as long as it is
    // empty. Neither shows up in a headless opacity trace and both are visible
    // on a phone. Updating text in place leaves the element, its children and
    // its compositing layer alone.
    var creditTextEl = null;
    var creditFilmEl = null;
    var creditKey = null;
    function setCredit(credit, film) {
      if (!creditEl) return;
      var c = (credit || '').trim();
      var f = (film || '').trim();
      var key = c + '\u0000' + f;
      if (key === creditKey) return;
      creditKey = key;
      if (!creditTextEl) {
        creditTextEl = document.createElement('span');
        creditTextEl.className = 'lc-scrolly-img__credit-text';
        creditFilmEl = document.createElement('span');
        creditFilmEl.className = 'lc-scrolly-img__film';
        creditEl.appendChild(creditTextEl);
        creditEl.appendChild(creditFilmEl);
      }
      creditTextEl.textContent = c;
      creditFilmEl.textContent = f;
      // hidden, not removed: the element must never go empty
      creditTextEl.hidden = !c;
      creditFilmEl.hidden = !f;
      if (c || f) creditEl.classList.add('is-shown');
      else creditEl.classList.remove('is-shown');
    }

    // Framing (LOS7-2728). A contained frame must be opaque in its own right:
    // the crossfade leaves the OUTGOING layer fully opaque underneath, so
    // without a paper ground of its own the previous photograph would show in
    // the letterbox margins. Cover frames keep the transparent layer they have
    // always had.
    function applyFraming(layer, fit, focal) {
      var contain = fit === 'contain';
      layer.style.backgroundSize = contain ? 'contain' : '';
      layer.style.backgroundColor = contain ? 'var(--paper)' : '';
      // Invalid values are dropped by the CSSOM, so a malformed authored focal
      // point degrades to the stylesheet's centre rather than breaking a frame.
      layer.style.backgroundPosition = '';
      if (focal) layer.style.backgroundPosition = focal;
    }

    var activeIdx = 0;
    var currentUrl = '';

    // LOS7-2921. Whether a crossfade is still running. The ADR-0008 invariant
    // (the outgoing layer stays fully opaque underneath, so composite coverage
    // is always 100%) holds for ONE fade; it breaks as soon as two overlap,
    // because the next swap's INCOMING layer is the one currently acting as the
    // opaque backdrop, and resetting it to opacity 0 leaves both layers part
    // transparent. Read the duration from the token rather than restating it,
    // so the two cannot drift apart.
    var fadeInFlight = false;
    var fadeTimer = null;
    var FADE_MS = (function () {
      var raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--scrolly-fade-in').trim();
      var n = parseFloat(raw);
      if (!n) return 700;
      return /ms$/.test(raw) ? n : n * 1000;
    })();

    function srcOf(cssUrl) {
      var m = String(cssUrl).match(/url\((['"]?)([^)'"]*)\1\)/);
      return m ? m[2] : null;
    }

    // Decode BEFORE the fade, never during it. A crossfade started on a bitmap
    // the browser has not decoded yet fades in an EMPTY layer: transparent on a
    // cover frame, so the outgoing photograph stays visible and the incoming one
    // snaps in partway through the fade, and `var(--paper)` on a contain frame,
    // which is the flash of white. Measured on a cold chapter at 390x844, with
    // no pre-pass to warm the cache: 28 of 29 crossfades began with the incoming
    // image uncached. Lengthening the fade to 700ms made that empty stretch
    // longer, not shorter, which is why it got MORE visible rather than less.
    //
    // The deadline matters as much as the wait: a frame must never be held
    // hostage to a slow network, so after 1.2s the swap proceeds regardless and
    // behaves exactly as it did before.
    function whenDecoded(src, cb) {
      if (!src) return cb();
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        cb();
      };
      var im = new Image();
      setTimeout(finish, 1200);
      im.onload = function () {
        if (im.decode) im.decode().then(finish, finish);
        else finish();
      };
      im.onerror = finish;
      im.src = src;
    }
    // Crossfade to a new url. The image swap is a no-op when the url is already
    // showing (consecutive captions can share a frame), keeping fetches minimal;
    // the credit still re-syncs so two frames sharing one image but carrying
    // different credits stay correct, and so does the framing, so two frames
    // on one image with different focal points still differ.
    // LOS7-2944. __setImage(null, ...) only clears the CREDIT -- both its
    // branches are gated on a truthy url -- so there was no way to take a
    // photograph OFF the layer. A map-only chapter needs one: with nothing of
    // its own to paint, the layer kept the previous chapter's frame underneath
    // the map for the chapter's whole run, and that is the image the map then
    // dissolved onto. Safe to clear only because the map is now raised while
    // the paper still covers (see update()), so nothing is ever exposed.
    imgLayer.__clearImage = function () {
      currentUrl = '';
      for (var ci = 0; ci < layers.length; ci++) {
        layers[ci].style.transition = 'none';
        layers[ci].style.opacity = '0';
        layers[ci].style.backgroundImage = '';
        void layers[ci].offsetWidth;
        layers[ci].style.transition = '';
      }
      fadeInFlight = false;
      if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
      setCredit('', '');
    };

    // Pull-back (item D). A map of image src to scale, published by the
    // resolver every tick. The scale goes on whichever layer is holding that
    // image, and follows it through a swap, so the frame fading out never
    // changes size and the one fading in arrives at its own scale. Every
    // other layer drops the property and reads the shared --lc-settle.
    var pullBySrc = {};
    function applyPull(layer) {
      var src = srcOf(layer.style.backgroundImage);
      var v = src ? pullBySrc[src] : null;
      if (v) layer.style.setProperty('--lc-layer-scale', v);
      else layer.style.removeProperty('--lc-layer-scale');
    }
    imgLayer.__setPullback = function (pulls) {
      pullBySrc = {};
      Object.keys(pulls || {}).forEach(function (u) {
        var src = srcOf(u);
        if (src) pullBySrc[src] = pulls[u];
      });
      for (var pi = 0; pi < layers.length; pi++) applyPull(layers[pi]);
    };

    imgLayer.__setImage = function (url, credit, film, fit, focal, instant) {
      if (url && url !== currentUrl) {
        // Claimed immediately so a later frame supersedes this one rather than
        // both swapping; the callback below re-checks before it touches the DOM.
        currentUrl = url;
        whenDecoded(srcOf(url), function () {
          if (currentUrl !== url) return; // a later frame won while we waited
          applySwap();
        });
        return;
      } else if (url) {
        applyFraming(layers[activeIdx], fit, focal);
      }
      setCredit(credit, film);

      function applySwap() {
        var incoming = layers[activeIdx === 0 ? 1 : 0];
        var outgoing = layers[activeIdx];
        var interrupted = fadeInFlight;
        // LOS7-2921. Land the outgoing layer BEFORE touching the incoming one.
        // It is about to become the opaque backdrop the incoming fades over,
        // and on a fast scroll it can still be part way through its own 700ms
        // fade. Skipping this is what exposed the paper ground: measured 0.232
        // composite coverage on the live theme, i.e. about 77% of the viewport
        // showing neither photograph.
        outgoing.style.transition = 'none';
        outgoing.style.opacity = '1';
        outgoing.style.zIndex = '1';
        // Reset incoming to transparent, on top, with the new image; no
        // transition for the reset so it doesn't animate from the old frame.
        incoming.style.transition = 'none';
        incoming.style.opacity = '0';
        incoming.style.zIndex = '2';
        incoming.style.backgroundImage = url;
        applyFraming(incoming, fit, focal);
        applyPull(incoming);
        // One reflow so BOTH resets land before we animate the fade-up.
        void incoming.offsetWidth;
        outgoing.style.transition = '';   // restore for when it is next incoming
        if (instant || interrupted) {
          // instant: hidden behind the map, so there is nothing to cross-fade
          // TO. interrupted: a fade was already running, so the reader is
          // flicking rather than reading, and the frame should track the scroll
          // instead of queueing another 700ms fade the next frame will cut off.
          // Landing it also stops a stale layer sitting half visible, which is
          // how a frame from an earlier chapter appeared near the end.
          incoming.style.opacity = '1';
          void incoming.offsetWidth;
          incoming.style.transition = '';
          fadeInFlight = false;
          if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
        } else {
          incoming.style.transition = '';   // restore the CSS opacity transition
          incoming.style.opacity = '1';
          fadeInFlight = true;
          if (fadeTimer) clearTimeout(fadeTimer);
          fadeTimer = setTimeout(function () {
            fadeInFlight = false;
            fadeTimer = null;
          }, FADE_MS);
        }
        activeIdx = activeIdx === 0 ? 1 : 0;
        // outgoing stays opaque underneath, no paper trough during the fade.
        setCredit(credit, film);
      }
    };

    // Prime the first layer with the opening frame — shown instantly, no fade
    // on first paint.
    var firstBg = sections[0].querySelector('.lc-scrolly__bg');
    if (firstBg) {
      var url = (portraitViewport() && firstBg.style.getPropertyValue('--bg-m')) ||
                firstBg.style.getPropertyValue('--bg-d');
      if (url) {
        layers[0].style.backgroundImage = url;
        layers[0].style.opacity = '1';
        layers[0].style.zIndex = '1';
        applyFraming(layers[0], firstBg.getAttribute('data-fit') || '',
          (firstBg.style.getPropertyValue('--focal') || '').trim());
        currentUrl = url;
        if (creditsEnabled) setCredit(firstBg.getAttribute('data-credit') || '', firstBg.getAttribute('data-film-line') || '');
      }
    }
    return imgLayer;
  }

  // Paper sections live inside a single host container at the end of <body>
  // so a rebuild can swap the whole set in one DOM operation. The earlier
  // pattern (querySelectorAll → forEach remove → append) left a zero-section
  // frame visible if a rebuild fired mid-scroll, exposing the fixed image
  // layer behind the article text — the visible "paper background
  // disappears" symptom. Two earlier approaches also failed: (a) body-bg
  // linear-gradient masks (browsers cap painted gradient height ~16-32k px
  // and long journals exceed that), (b) a single fixed paper div with a
  // JS-updated mask-image (lags one frame behind native scroll → visible
  // jitter on the heading/image seam). Per-range absolute divs in body
  // coords avoid both.
  function getPaperHost() {
    var host = document.getElementById('lc-scrolly-paper-host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'lc-scrolly-paper-host';
      document.body.appendChild(host);
    }
    return host;
  }

  // markEdges (item C, 2026-09-26): a paper section whose top edge is a
  // chapter's bottom casts a soft shadow up onto the photograph, and one whose
  // bottom edge is a chapter's top casts it down. Every range except the one
  // that opens the page follows a chapter, and every range except the one
  // that closes it precedes one, because the ranges are exactly the gaps
  // between chapters. Paper meeting paper, the article's own sections and the
  // page end get nothing.
  function buildPaperSections(sections, markEdges) {
    var host = getPaperHost();
    var ordered = Array.prototype.slice.call(sections).map(function (s) {
      var r = s.getBoundingClientRect();
      var top = r.top + window.scrollY;
      return { top: top, bot: top + r.height };
    }).sort(function (a, b) { return a.top - b.top; });
    var ranges = [];
    var cursor = 0;
    var afterChapter = false;
    ordered.forEach(function (s) {
      if (s.top > cursor) {
        ranges.push({ top: cursor, height: s.top - cursor, after: afterChapter, before: true });
      }
      cursor = s.bot;
      afterChapter = true;
    });
    var bodyH = document.body.scrollHeight;
    if (cursor < bodyH) ranges.push({ top: cursor, height: bodyH - cursor, after: afterChapter, before: false });
    var frag = document.createDocumentFragment();
    ranges.forEach(function (r) {
      var div = document.createElement('div');
      div.className = 'lc-scrolly-paper-section';
      if (markEdges && r.after) div.classList.add('is-after-chapter');
      if (markEdges && r.before) div.classList.add('is-before-chapter');
      div.style.top = r.top + 'px';
      div.style.height = r.height + 'px';
      frag.appendChild(div);
    });
    // Atomic swap: host is never empty mid-rebuild.
    host.replaceChildren ? host.replaceChildren(frag)
                         : (host.innerHTML = '', host.appendChild(frag));
  }

  // Paper-coverage guarantee (ADR-0008 follow-up). The fixed image must only
  // ever change while the viewport is fully over paper; if a chapter's intro
  // paper band is shorter than the viewport, the image bleeds above and below
  // it and the frame swap is visible. Pad each block's preceding paper run so
  // every inter-chapter gap is at least the viewport plus a fixed safety, at
  // all widths and heights. Exact (adds only the deficit) and idempotent, so it
  // converges rather than fighting the rebuild. A viewport-relative copy
  // guardrail can never be met by fixed-px copy alone (text is shortest at wide
  // viewports), so this is the mechanical floor; richer chapter copy reduces how
  // much paper it has to add.
  //
  // The safety is a FIXED pixel headroom, not a ratio of the viewport. The thing
  // it guards against is late content reflow (a chapter intro can shrink after
  // this pass computes, e.g. a font swap or an image settling), which moves
  // content by a roughly fixed number of px regardless of viewport height; a
  // ratio over-pads tall windows (1.14 x 1400 buried 196px of blank paper under
  // a thin chapter) while under-padding short ones. rebuild-on-height-change
  // recomputes against the settled layout, so the safety only has to cover the
  // window between this pass and that rebuild. The deficit is added as margin-top
  // on the block (the paper sections then cover it); the lasting fix for the
  // blank is richer chapter copy, which makes this floor non-binding.
  var COVER_SAFETY = 120;
  function applyPaperCoverage(orderedBlocks) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var need = vh + COVER_SAFETY;
    orderedBlocks.forEach(function (s) { s.style.marginTop = ''; });
    var prevBottom = null;
    orderedBlocks.forEach(function (s) {
      var r = s.getBoundingClientRect();
      var top = r.top + window.scrollY;
      var gap = (prevBottom === null) ? top : (top - prevBottom);
      if (gap < need) {
        s.style.marginTop = Math.round(need - gap) + 'px';
        r = s.getBoundingClientRect();
      }
      prevBottom = r.bottom + window.scrollY;
    });
  }

  function wirePaperRebuild(sections, markEdges) {
    var ordered = Array.prototype.slice.call(sections).sort(function (a, b) {
      return (a.getBoundingClientRect().top + window.scrollY) -
             (b.getBoundingClientRect().top + window.scrollY);
    });
    var timer = null;
    var lastBodyH = -1;
    function rebuild() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        applyPaperCoverage(ordered);
        buildPaperSections(sections, markEdges);
        lastBodyH = document.body.scrollHeight;
      }, 80);
    }
    // ADR-0008 Thread 5: a ResizeObserver(document.body) fires on ANY layout
    // delta, and scripted/momentum scrolling churns it (lazy image decode,
    // sub-pixel reflow) far more than body height actually changes, so each
    // callback forced a full paper recompute. Gate the RO path on a real
    // scrollHeight delta; the other triggers (resize, fonts, image load) still
    // rebuild unconditionally because they can move content without changing
    // total height.
    function rebuildIfHeightChanged() {
      if (document.body.scrollHeight !== lastBodyH) rebuild();
    }
    applyPaperCoverage(ordered);
    buildPaperSections(sections, markEdges);
    lastBodyH = document.body.scrollHeight;

    // Rebuild on every signal that body height could have shifted:
    // - window load: late assets finish (deferred scripts, slow images)
    // - resize: orientation change, devtools toggling
    // - ResizeObserver: any body-height delta including font swap (FOIT→FOUT)
    //   and lazy image decode that didn't fire a window resize event
    // - document.fonts.ready: web font swap can re-flow paragraph heights
    // - per-image load: each in-flight article img settling
    // Keep the reader's place across a resize (LOS7-2795).
    //
    // Chapters are sized in vh, so a window-height change rescales every
    // caption slot. Measured live: the five scrolly chapters total 104,051px at
    // 1680x1000 and 79,061px at 1680x700, so dragging 300px of height moves
    // 26,000px of document. The browser preserves scrollY, not narrative
    // position, so the same scrollY lands somewhere else in the story: at
    // scrollY 6783 the reader was on "So I drove to Seto." and after the height
    // change was two captions further on, without touching the scroll wheel.
    //
    // The anchor is captured on SCROLL, not on resize. By the time any resize
    // listener runs the new viewport is already in effect, so a
    // getBoundingClientRect() there measures the NEW layout: an earlier attempt
    // captured in the resize handler and every measured delta came back exactly
    // 0. Holding the reader's last scrolled position is the only way to have a
    // "before" to restore to.
    var anchorEl = null;
    var anchorTop = 0;
    var anchorSettle = null;
    var anchorList = null;
    var capturePending = false;
    var restoring = false;
    var expectedY = -1;

    function anchorCandidates() {
      if (!anchorList || !anchorList.length) {
        anchorList = document.querySelectorAll(
          '.lc-scrolly__caption, .prose-block, figure.fig'
        );
      }
      return anchorList;
    }

    function captureAnchor() {
      var els = anchorCandidates();
      var best = null;
      var bestTop = -Infinity;
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        // the last block to have started above the fold: what the reader is on
        if (r.top <= 8 && r.top > bestTop && r.bottom > 0) {
          bestTop = r.top;
          best = els[i];
        }
      }
      if (!best) {
        // nothing has crossed the top yet, so anchor on the first block below it
        for (var j = 0; j < els.length; j++) {
          var q = els[j].getBoundingClientRect();
          if (q.top > 8) { best = els[j]; bestTop = q.top; break; }
        }
      }
      if (best) {
        anchorEl = best;
        anchorTop = bestTop;
      }
    }

    function restoreAnchor() {
      if (!anchorEl) return;
      var delta = anchorEl.getBoundingClientRect().top - anchorTop;
      if (Math.abs(delta) > 0.5) {
        expectedY = Math.round(window.scrollY + delta);
        window.scrollTo(0, expectedY);
      }
    }

    function endRestore() {
      if (anchorSettle) window.clearTimeout(anchorSettle);
      anchorSettle = null;
      restoring = false;
      anchorList = null;
    }

    // A scroll we caused lands exactly on expectedY. Anything else is the
    // reader, and the reader always wins: they get to scroll away during the
    // settle window rather than being dragged back to where they were.
    window.addEventListener('scroll', function () {
      if (restoring) {
        if (Math.round(window.scrollY) === expectedY) return;
        endRestore();
      }
      if (capturePending) return;
      capturePending = true;
      window.requestAnimationFrame(function () {
        capturePending = false;
        if (!restoring) captureAnchor();
      });
    }, { passive: true });

    window.addEventListener('resize', function () {
      restoring = true;
      window.requestAnimationFrame(restoreAnchor);
      if (anchorSettle) window.clearTimeout(anchorSettle);
      anchorSettle = window.setTimeout(function () {
        restoreAnchor();
        endRestore();
      }, 220);
    }, { passive: true });

    window.addEventListener('load', rebuild);
    window.addEventListener('resize', rebuild);
    if ('ResizeObserver' in window) {
      new ResizeObserver(rebuildIfHeightChanged).observe(document.body);
    }
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(rebuild).catch(function () { /* swallow */ });
    }
    var imgs = document.querySelectorAll('article.article img');
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.complete) return;
      img.addEventListener('load',  rebuild, { once: true });
      img.addEventListener('error', rebuild, { once: true });
    });
  }

  // ----- Cinematic hero pan -------------------------------------------------
  // .lc-hero--pan is added server-side in Liquid (sections/main-article.liquid)
  // when template.suffix == 'scrollytelling'. JS only triggers the animation
  // (.is-panning) after window load, so the hero box dimensions are stable
  // from first paint and never cause CLS. The classList.add below is a
  // fallback for legacy article templates where the class isn't preset.
  function wireHeroPan() {
    var artHero = document.querySelector('.art-hero');
    if (!artHero) return;
    if (!artHero.classList.contains('lc-hero--pan')) artHero.classList.add('lc-hero--pan');
    // Video hero (LOS7-2659): the <video> covers the img, so the pan would
    // only burn compositor time under it. Skip the animation class.
    if (artHero.querySelector('video.art-hero__video')) return;
    function doPan() { artHero.classList.add('is-panning'); }
    if (document.readyState === 'complete') doPan();
    else window.addEventListener('load', doPan);
  }

  // ----- Map frames on the static fallback ---------------------------------
  // LOS7-2659. Map frames (index 0 of a journey-map chapter) have no image;
  // on the static fallback (reduced motion, or up to 749px, where
  // lc-journey-map.js renders one inline map instead) they would paint as
  // an empty box, so strip them BEFORE caption hydration: remaining frames
  // re-index from 0 and a chapter left with no frames loses its block
  // entirely, so its paragraphs stay on the paper as ordinary prose.
  function stripMapFrames(sections) {
    Array.prototype.forEach.call(sections, function (el) {
      // A phased chapter keeps its map frames on every path. Its whole story
      // is the map, so stripping it here would leave the beats pointing at
      // frames that no longer exist and the chapter would render as the
      // photograph run with the map missing.
      if (el.hasAttribute('data-phases')) return;
      var maps = el.querySelectorAll('.lc-scrolly__bg[data-map]');
      if (!maps.length) return;
      Array.prototype.forEach.call(maps, function (m) { m.parentNode.removeChild(m); });
      el.removeAttribute('data-map-chapter');
      var bgs = el.querySelectorAll('.lc-scrolly__bg');
      if (!bgs.length) {
        if (el.parentNode) el.parentNode.removeChild(el);
        return;
      }
      Array.prototype.forEach.call(bgs, function (bg, i) {
        bg.setAttribute('data-scrolly-index', String(i));
        if (i === 0) bg.classList.remove('is-hidden');
        else bg.classList.add('is-hidden');
      });
    });
  }

  // ----- Init ---------------------------------------------------------------
  function init() {
    var scrollySections = document.querySelectorAll('.lc-scrolly[data-anchor]');
    if (!scrollySections.length) return;

    var root = document.getElementById('articleBody');
    if (!root) return;

    var mode = resolveMode();
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Same boundary lc-journey-map.js uses to pick its static inline map.
    var staticMap = reduced || window.matchMedia('(max-width: 749px)').matches;
    if (staticMap) {
      stripMapFrames(scrollySections);
      scrollySections = document.querySelectorAll('.lc-scrolly[data-anchor]');
      if (!scrollySections.length) return;
    }

    // Hero pan + body class always run on scrolly pages — CSS uses
    // body.lc-scrolly-page to hide breadcrumbs and expand hero height
    // regardless of mode / motion / viewport.
    document.body.classList.add('lc-scrolly-page');
    document.body.setAttribute('data-scrolly-mode', mode);
    wireHeroPan();

    // Hydrate captions for every scrolly block (cheap, works on all
    // form factors — the rendered captions are visible in the mobile
    // fallback layout too).
    scrollySections.forEach(function (scrollyEl) {
      hydrateCaptions(root, scrollyEl);
    });

    // Motion-OK: wire the fixed image layer and per-section observer on
    // every viewport. Mobile uses the same architecture as desktop so the
    // image appears stationary while paper sections and captions pass over
    // it. Only reduced-motion falls back to stacked inline .lc-scrolly__bg
    // images via CSS — no JS observer needed there.
    // A PHASED chapter is wired even under reduced motion: its line is driven
    // by scroll position rather than animated, which suits that preference
    // better than the stacked fallback does, and its camera moves and paper
    // wipe are instant there. Scoped to phased blocks only, so every other
    // chapter and every other article keeps today's stacked inline path.
    var phasedSections = Array.prototype.slice.call(
      document.querySelectorAll('.lc-scrolly[data-anchor][data-phases]')
    );
    if (reduced && !phasedSections.length) return;

    var liveSections = reduced
      ? phasedSections
      : Array.prototype.slice.call(document.querySelectorAll('.lc-scrolly[data-anchor]'));
    if (!liveSections.length) return;

    // Credits enabled = the article-images section's render_credit_blocks
    // setting (emitted as data-render-credits). Gates the scrolly credit
    // overlay; the inline figcaption credit reads the same flag in
    // lc-article-postprocess.js.
    var imgSection = document.querySelector('.lc-article-images');
    var creditsEnabled = !!(imgSection &&
      imgSection.getAttribute('data-render-credits') === 'true');

    // Settle, pull-back and the paper shadow (items C and D) are motion: off
    // under reduced motion, where only phased chapters are wired at all, and
    // off in immersive mode, which has no paper for them to belong to.
    var motion = !reduced && mode === 'interleave';
    setupImageLayer(liveSections, creditsEnabled);
    wireFrameResolver(liveSections, creditsEnabled, motion);

    // Paper sections are an interleave-mode device — they mask the
    // fixed image whenever the user is in body-text territory. Immersive
    // mode wants the image visible throughout, so we skip paper entirely
    // and let the body text overlay it (CSS scrim on captions handles
    // legibility).
    if (mode === 'interleave') {
      wirePaperRebuild(liveSections, motion);
    }
  }

  // Wait for the postprocessor's rebuild+figure-placement to finish so
  // h2 chapter headings and figure DOM order are stable before we hydrate.
  // Idempotency guard: if this script is ever loaded twice (a known historic
  // Liquid bug — two <script> tags for lc-scrollytelling.js), both IIFEs
  // execute. The guard ensures init runs exactly once regardless.
  ready(function () {
    window.LCArticle.onReady(function () {
      if (window.__lcScrollyInited) return;
      window.__lcScrollyInited = true;
      init();
    });
  });
})();
